import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const ConnectedAccount = () => {
  const [open, setOpen] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [userPlan, setUserPlan] = useState("Basic");
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const tenantApiUrl = process.env.REACT_APP_TENANT_API || "https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod";

  const accountData = [
    {
      id: 1,
      name: "AWS",
      description: "Amazon Web Services",
      logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
    },
    {
      id: 2,
      name: "GCP",
      description: "Google Cloud Services",
      logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
    },
    {
      id: 3,
      name: "Azure",
      description: "Microsoft Cloud",
      logo: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg",
    },
  ];

  const availableAccounts =
    userPlan === "Basic"
      ? accountData.slice(0, 1) // Only AWS for Basic plan
      : accountData; // All accounts for other plans

  const handleCardClick = (account) => {
    setSelectedAccount(account);
    setUpdateMode(false);
    setOpen(true);
  };

  const loadConnectedAccounts = async () => {
    try {
      const tenantID = localStorage.getItem("tenantID");
      if (!tenantID) return;

      setLoading(true);
      
      const response = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error("Failed to fetch accounts");

      const userData = await response.json();
      if (userData.cloudAccounts) {
        const cloudAccountsData = typeof userData.cloudAccounts === 'string' 
          ? JSON.parse(userData.cloudAccounts) 
          : userData.cloudAccounts;
        const awsAccounts = (cloudAccountsData.aws || []).map(account => {
          const arnParts = account.iamrole.split(":");
          const awsAccountId = arnParts.length >= 5 ? arnParts[4] : "Unknown";
          return {
            id: account.id || account.alias,
            name: "AWS",
            description: account.alias || account.id,
            logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
            iamrole: account.iamrole,
            awsAccountId: awsAccountId,
            alias: account.alias || account.id,
          };
        });
        setConnectedAccounts(awsAccounts);
      }
    } catch (error) {
      console.error("Error loading accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectAccount = async () => {
    if (!selectedAccount) return;
    if (!selectedAccount.iamrole) {
      Swal.fire("Error", "Please enter the IAM Role ARN", "error");
      return;
    }

    setLoading(true);
    try {
      const tenantID = localStorage.getItem("tenantID");
      if (!tenantID) return;
      
      const existingResponse = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!existingResponse.ok) throw new Error("Failed to fetch user data");

      const existingData = await existingResponse.json();
      const existingCloudAccounts = typeof existingData.cloudAccounts === 'string' 
        ? JSON.parse(existingData.cloudAccounts) 
        : (existingData.cloudAccounts || {});

      if (!existingCloudAccounts.aws) existingCloudAccounts.aws = [];
      
      // Extract accountId from IAM role ARN
      const arnParts = selectedAccount.iamrole.split(':');
      const accountId = arnParts.length >= 5 ? arnParts[4] : null;
      
      existingCloudAccounts.aws.push({
        id: selectedAccount.id,
        accountId: accountId,
        iamrole: selectedAccount.iamrole,
        alias: selectedAccount.id
      });

      // Update data
      const response = await fetch(`${tenantApiUrl}/v2/tenant/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantID,
          cloudAccounts: existingCloudAccounts,
        }),
      });

      if (!response.ok) throw new Error("Failed to connect account");

      setOpen(false);
      await loadConnectedAccounts();

      Swal.fire({
        title: "Connected!",
        text: `${selectedAccount.name} has been successfully connected.`,
        icon: "success",
      });
    } catch (error) {
      console.error("Error connecting account:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to connect account. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccount = async () => {
    setLoading(true);
    try {
      const tenantID = localStorage.getItem("tenantID");
      if (!tenantID) return;
      
      const existingResponse = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`);

      if (!existingResponse.ok) throw new Error("Failed to fetch user data");

      const existingData = await existingResponse.json();
      const existingCloudAccounts = typeof existingData.cloudAccounts === 'string' 
        ? JSON.parse(existingData.cloudAccounts) 
        : (existingData.cloudAccounts || {});

      if (!existingCloudAccounts.aws) existingCloudAccounts.aws = [];
      const accountIndex = existingCloudAccounts.aws.findIndex(acc => acc.id === selectedAccount.id);
      if (accountIndex !== -1) {
        const arnParts = selectedAccount.iamrole.split(':');
        const accountId = arnParts.length >= 5 ? arnParts[4] : null;
        existingCloudAccounts.aws[accountIndex] = {
          iamrole: selectedAccount.iamrole,
          alias: selectedAccount.id,
          id: selectedAccount.id,
          accountId: accountId
        };
      }

      const response = await fetch(`${tenantApiUrl}/v2/tenant/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantID, cloudAccounts: existingCloudAccounts }),
      });

      if (!response.ok) throw new Error("Failed to update account");

      setOpen(false);
      await loadConnectedAccounts();

      Swal.fire({
        title: "Updated!",
        text: `${selectedAccount.name} has been successfully updated.`,
        icon: "success",
      });
    } catch (error) {
      console.error("Error updating account:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update account. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnectivity = async (account) => {
    setLoading(true);
    try {
      const orgApiUrl = process.env.REACT_APP_ORG_API;
      const response = await fetch(`${orgApiUrl}/test-connectivity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roleArn: account.iamrole,
          accountId: account.awsAccountId,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        Swal.fire({
          title: "Connection Successful!",
          text: `Successfully connected to ${account.alias}`,
          icon: "success",
        });
      } else {
        Swal.fire({
          title: "Connection Failed",
          text: result.error || "Unable to assume role. Please check your IAM role configuration.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("Error testing connectivity:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to test connectivity. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = (account) => {
    Swal.fire({
      title: "Confirm Deletion",
      text: `Are you sure you want to delete ${account.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const tenantID = localStorage.getItem("tenantID");
          if (!tenantID) return;
          
          const existingResponse = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`);

          if (!existingResponse.ok) throw new Error("Failed to fetch user data");

          const existingData = await existingResponse.json();
          const existingCloudAccounts = typeof existingData.cloudAccounts === 'string' 
            ? JSON.parse(existingData.cloudAccounts) 
            : (existingData.cloudAccounts || {});

          if (existingCloudAccounts.aws && Array.isArray(existingCloudAccounts.aws)) {
            existingCloudAccounts.aws = existingCloudAccounts.aws.filter(
              (acc) => acc.id !== account.id && acc.alias !== account.alias && acc.iamrole !== account.iamrole
            );
          }

          const response = await fetch(`${tenantApiUrl}/v2/tenant/update`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tenantID, cloudAccounts: existingCloudAccounts }),
          });

          if (!response.ok) throw new Error("Failed to delete account");

          await loadConnectedAccounts();

          Swal.fire({
            title: "Deleted!",
            text: `${account.name} has been successfully removed.`,
            icon: "success",
          });
        } catch (error) {
          console.error("Error deleting account:", error);
          Swal.fire({
            title: "Error",
            text: "Failed to delete account. Please try again.",
            icon: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const storedPlanString = localStorage.getItem("selectedPlan");
    if (storedPlanString) {
      try {
        const storedPlan = JSON.parse(storedPlanString);
        if (storedPlan && storedPlan.name) {
          setUserPlan(storedPlan.name);
        }
      } catch (e) {
        console.error("Error parsing selectedPlan");
      }
    }
    loadConnectedAccounts();
  }, []);

  return (
    <Box
      sx={{
        pt: 8,
        backgroundColor: "#f9f9f9",
        minHeight: "100vh",
        color: "#333",
        position: "relative",
      }}
    >
      <Typography
        variant="h4"
        sx={{ mb: 4, color: "#342ead", textAlign: "center" }}
      >
        Connected Accounts
      </Typography>

      {loading && (
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center", 
          my: 4 
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: "#342ead", fontWeight: "bold" }}>
            Loading data, please wait...
          </Typography>
        </Box>
      )}

      <Typography
        variant="h6"
        sx={{ mb: 4, textAlign: "center", color: "#666" }}
      >
        Your Plan: <strong>{userPlan}</strong>
      </Typography>

      {/* Table Section */}
      <Box
        sx={{
          width: "100%",
          p: 2,
          pb: 10,
        }}
        className="container mt-5"
      >
        <Typography
          variant="h5"
          sx={{ mb: 2, color: "#342ead", textAlign: "center" }}
        >
          Connected Accounts Table
        </Typography>
        <TableContainer
  component={Paper}
  sx={{
    borderRadius: 3,
    overflowX: "auto", // Enables horizontal scrolling on small screens if needed
    width: "100%",
  }}
>
  <Table
    sx={{
      minWidth: { xs: "100%", sm: "600px" }, // Minimum width for larger screens
    }}
  >
    <TableHead sx={{ backgroundColor: "#342ead" }}>
      <TableRow>
        <TableCell className="text-center"
          sx={{
            color: "#fff",
            fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
            padding: { xs: "4px", sm: "8px", md: "16px" }, // Adjust padding based on screen size
            width: "15%", // Ensure proper distribution
          }}
        >
          ID
        </TableCell>
        <TableCell
          sx={{
            color: "#fff",
            fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
            padding: { xs: "4px", sm: "8px", md: "16px" },
            width: "30%",
          }}
        >
          Account Name
        </TableCell>
        <TableCell
          sx={{
            color: "#fff",
            fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
            padding: { xs: "4px", sm: "8px", md: "16px" },
            width: "40%",
          }}
        >
          Description
        </TableCell>
        <TableCell
          sx={{
            color: "#fff",
            fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
            padding: { xs: "4px", sm: "8px", md: "16px" },
            width: "10%",
          }}
        >
          Actions
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {connectedAccounts.map((account) => (
        <TableRow key={account.id}>
          <TableCell className="text-center"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
              padding: { xs: "4px", sm: "8px", md: "16px" },
            }}
          >
            {account.id}
          </TableCell>
          <TableCell
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
              padding: { xs: "4px", sm: "8px", md: "16px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap", // Allows wrapping on small screens
              }}
            >
              <img
                src={account.logo}
                alt={account.name}
                style={{
                  width: "50px",
                  height: "50px", // Adjust image size for smaller screens
                }}
              />
              {account.name}
            </Box>
          </TableCell>
          <TableCell
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
              padding: { xs: "4px", sm: "8px", md: "16px" },
            }}
          >
            {account.description}
          </TableCell>
          <TableCell
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.9rem", md: "1rem" },
              padding: { xs: "4px", sm: "8px", md: "16px" },
              display:{ xs: "flex-column", sm: "flex-column", md: "flex" },
            }}
          >
            <Button
              className="my-1"
              variant="contained"
              color="success"
              size="small"
              sx={{
                mr: 1,
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                padding: { xs: "2px 6px", sm: "4px 8px", md: "6px 12px" },
              }}
              onClick={() => handleTestConnectivity(account)}
            >
              Test
            </Button>
            <Button
              className="my-1"
              variant="outlined"
              size="small"
              sx={{
                mr: 1,
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                padding: { xs: "2px 6px", sm: "4px 8px", md: "6px 12px" },
              }}
              onClick={() => {
                setUpdateMode(true);
                setSelectedAccount(account);
                setOpen(true);
              }}
            >
              Update
            </Button>
            <Button
              className="my-1"
              variant="contained"
              color="error"
              size="small"
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.8rem", md: "0.9rem" },
                padding: { xs: "2px 6px", sm: "4px 8px", md: "6px 12px" },
              }}
              onClick={() => handleDeleteAccount(account)}
            >
              Delete
            </Button>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>

      </Box>

      {/* Cards Section */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          alignItems: "center",
          gap: 4,
        }}
      >
        {availableAccounts.map((account) => (
          <Card
            key={account.id}
            sx={{
              width: 300,
              height: 180,
              boxShadow: 3,
              backgroundColor: "#fff",
              borderRadius: 3,
              textAlign: "center",
              transition: "transform 0.3s, box-shadow 0.3s",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: 6,
                cursor: "pointer",
              },
            }}
            onClick={() => handleCardClick(account)}
          >
            <CardContent>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <img
                  src={account.logo}
                  alt={account.name}
                  style={{ width: "60px", height: "60px" }}
                />
              </Box>
              <Typography variant="h6">{account.name}</Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 1 }}
              >
                {account.description}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(5px)",
        }}
      >
        <Box
          sx={{
            width: "80%",
            maxWidth: "800px",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Typography id="modal-title" variant="h6" sx={{ mb: 2 }}>
            {updateMode
              ? `Update Details for ${selectedAccount?.name}`
              : `Connect to ${selectedAccount?.name}`}
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: "#FF0000", borderRadius: 2, textAlign: "left" }}>
            <Typography variant="h4" sx={{ color: "#fff" }}>
              TEST - If you see this RED box, the code is working!
            </Typography>
          </Box>
          
          {!updateMode && selectedAccount?.name === "AWS" && (
            <Box sx={{ mb: 3, p: 2, bgcolor: "#f0f7ff", borderRadius: 2, textAlign: "left" }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold" }}>
                Step 1: Create IAM Role in Your AWS Account
              </Typography>
              <Button
                variant="contained"
                size="small"
                href="https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirCustomerOnboardingCloudFormation.yml&stackName=odir-integration"
                target="_blank"
                sx={{
                  backgroundColor: "#FF9900",
                  color: "white",
                  mb: 2,
                  "&:hover": { backgroundColor: "#EC7211" },
                }}
              >
                Launch CloudFormation Stack
              </Button>
              <Typography variant="caption" display="block" sx={{ mb: 2, color: "#666" }}>
                This will open AWS Console and create the required IAM role (OdirScanReadOnlyRole)
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Step 2: Copy Role ARN from CloudFormation Outputs and paste below
              </Typography>
            </Box>
          )}
          
          <TextField
            label={selectedAccount?.name === "AWS" ? "IAM Role ARN" : "Role ARN"}
            placeholder={selectedAccount?.name === "AWS" ? "arn:aws:iam::123456789012:role/OdirScanReadOnlyRole" : ""}
            variant="outlined"
            fullWidth
            value={selectedAccount?.iamrole || ""}
            sx={{ mb: 3 }}
            onChange={(e) =>
              setSelectedAccount({
                ...selectedAccount,
                iamrole: e.target.value,
                id: e.target.value.split("/").pop() || e.target.value,
              })
            }
          />
          <Button
            variant="contained"
            onClick={updateMode ? handleUpdateAccount : handleConnectAccount}
            sx={{
              backgroundColor: "#342ead",
              color: "white",
              "&:hover": {
                backgroundColor: "#1d1e5a",
              },
            }}
          >
            {updateMode ? "Update" : "Connect"}
          </Button>
        </Box>
      </Modal>

      {/* Go to Dashboard Button */}
      {connectedAccounts.length > 0 && (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/dashboard/home")}
            sx={{
              backgroundColor: "#342ead",
              color: "white",
              "&:hover": {
                backgroundColor: "#1d1e5a",
              },
              px: 4,
              py: 1,
              fontSize: "1rem",
              fontWeight: "bold",
            }}
          >
            Go to Dashboard
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ConnectedAccount;
